import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, ReferenceLine, Legend,
} from "recharts";
import { Truck, Gauge, AlertTriangle, Activity, Loader2, Download, ArrowUpRight } from "lucide-react";
import { api, fmtNum, fmtINR } from "@/lib/api";
import { useAlertStreamContext } from "@/context/AlertStreamContext";
import PageHeader from "@/components/layout/PageHeader";
import GlassCard from "@/components/ui-fleet/GlassCard";
import ClickableKPITile from "@/components/ui-fleet/ClickableKPITile";
import StatusBadge from "@/components/ui-fleet/StatusBadge";
import DrillDownPanel from "@/components/ui-fleet/DrillDownPanel";
import StatTableDrillDown from "@/components/ui-fleet/StatTableDrillDown";

const COLORS = {
  copper: "#C4622D",
  copperLight: "#E8884A",
  slate: "#2C2825",
  slate500: "#7A726A",
  green: "#2D7A4A",
  amber: "#B07D1A",
  red: "#B03A2A",
  blue: "#3B6FA8",
};

const STAGE_COLORS = {
  Load: COLORS.copper,
  Haul: COLORS.copperLight,
  Queue: COLORS.amber,
  Return: COLORS.slate500,
};

const TKPH_BENCHMARK_LPT = 1.8;

const DRILL_META = {
  fleet:      { title: "Total Dumpers",         subtitle: "BD-series fleet inventory" },
  cycles:     { title: "Cycles Today",          subtitle: "Per-machine cycles ranked highest first" },
  tonnes:     { title: "Tonnes Today",          subtitle: "Per-machine tonnage hauled" },
  fpt:        { title: "Fleet L / Tonne",       subtitle: "Fuel-per-tonne ranked worst first" },
  overloads:  { title: "Overload Incidents",    subtitle: "Recent overloads · acknowledge in the main log" },
  tkph:       { title: "TKPH Warnings",         subtitle: "Tyre-load index vs rated TKPH" },
  haul:       { title: "Haul Cycle Breakdown",  subtitle: "Where minutes are lost — top contenders first" },
  tyre:       { title: "Tyre Health Index",     subtitle: "Full TKPH ranking + tyre value at risk" },
  fuel:       { title: "Fuel per Tonne",        subtitle: "Benchmark 1.8 L/t — gap surfaced per machine" },
  production: { title: "Production vs Target",  subtitle: "7-day attainment with variance against target" },
};

export default function DumperIntelligence() {
  const stream = useAlertStreamContext();
  const [mines, setMines] = useState([]);
  const [mine, setMine] = useState("all");
  const [kpis, setKpis] = useState(null);
  const [fleet, setFleet] = useState([]);
  const [overloads, setOverloads] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acking, setAcking] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  // Drill-down state — only one panel open at a time
  const [drill, setDrill] = useState(null); // 'fleet'|'cycles'|'tonnes'|'fpt'|'overloads'|'tkph'|'haul'|'tyre'|'fuel'|'production'

  const fetchAll = async (mineId) => {
    setLoading(true);
    try {
      const params = mineId && mineId !== "all" ? { mine: mineId } : {};
      const [kRes, mRes, oRes, opRes] = await Promise.all([
        api.get("/dumper/fleet-kpis", { params }),
        api.get("/dumper/machines", { params }),
        api.get("/dumper/overloads"),
        api.get("/dumper/operator-scorecard", { params }),
      ]);
      setKpis(kRes.data);
      setFleet(mRes.data);
      setOverloads(oRes.data);
      setOperators(opRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/dumper/mines").then((r) => setMines(r.data));
    fetchAll("all");
  }, []);

  useEffect(() => { fetchAll(mine); }, [mine]);

  // ── Live stream: prepend any DUMPER_* alerts as overload-log entries ──
  useEffect(() => {
    if (!stream.alerts || stream.alerts.length === 0) return;
    const liveOverloads = stream.alerts
      .filter((a) => a.alert_type === "DUMPER_OVERLOAD")
      .map((a) => ({
        id: a.id,
        serial_number: a.serial_number || "EQP-?",
        ts: a.created_at,
        actual_payload_t: a.actual_payload_t || "—",
        rated_payload_t: a.rated_payload_t || "—",
        over_by_pct: a.over_by_pct || "—",
        operator: a.operator || "—",
        acknowledged: !!a.acknowledged,
        _live: true,
      }));
    if (liveOverloads.length === 0) return;
    setOverloads((prev) => {
      const seen = new Set(prev.map((o) => o.id));
      const additions = liveOverloads.filter((o) => !seen.has(o.id));
      return [...additions, ...prev].slice(0, 100);
    });
  }, [stream.alerts]);

  const onAck = async (overloadId) => {
    setAcking(overloadId);
    try {
      await api.post(`/dumper/overloads/${overloadId}/acknowledge`);
      setOverloads((o) => o.map((x) => (x.id === overloadId ? { ...x, acknowledged: true } : x)));
    } finally {
      setAcking(null);
    }
  };

  const downloadShiftReport = async () => {
    setReportLoading(true);
    try {
      const params = mine && mine !== "all" ? { mine } : {};
      const r = await api.get("/dumper/shift-report", { params });
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `opsora_shift_report_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setReportLoading(false);
    }
  };

  // Top 5 dumpers by total cycle time, for stacked bar
  const cycleBreakdown = useMemo(() => {
    return fleet
      .slice()
      .sort((a, b) => (b.avg_cycle_min || 0) - (a.avg_cycle_min || 0))
      .slice(0, 5)
      .map((m) => {
        // Approximate stage split from avg_cycle_min using global proportions ~ Load:15%, Haul:40%, Queue:10%, Return:35%
        const t = m.avg_cycle_min || 0;
        return {
          name: m.serial_number.replace("BEML-", ""),
          Load:   Math.round(t * 0.15),
          Haul:   Math.round(t * 0.40),
          Queue:  Math.round(t * 0.10),
          Return: Math.round(t * 0.35),
        };
      });
  }, [fleet]);

  const fuelData = useMemo(
    () =>
      fleet.slice(0, 12).map((m) => ({
        name: m.serial_number.replace("BEML-", ""),
        lpt: m.fuel_per_tonne,
      })),
    [fleet],
  );

  const productionTrend = useMemo(() => {
    // Build 7-day from kpis.total_tonnes_today × deterministic variance
    const today = kpis?.total_tonnes_today || 0;
    return Array.from({ length: 7 }, (_, i) => {
      const offset = (Math.sin((i + 1) * 1.7) + Math.cos(i * 0.8)) * 0.08;
      return {
        day: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][i],
        actual: Math.round(today * (1 + offset)),
        target: Math.round(today * 0.95),
      };
    });
  }, [kpis]);

  const tkphRows = useMemo(
    () =>
      fleet.map((m) => {
        const ratio = m.rated_tkph ? m.tkph / m.rated_tkph : 0;
        const status = ratio >= 0.95 ? "CRITICAL" : ratio >= 0.85 ? "WARNING" : "OK";
        return { ...m, ratio, status };
      }),
    [fleet],
  );

  if (loading && !kpis) {
    return (
      <div className="page-content flex items-center justify-center h-[60vh]" data-testid="dumper-loading">
        <Loader2 size={28} className="animate-spin" style={{ color: COLORS.copper }} />
      </div>
    );
  }

  return (
    <div className="page-content space-y-6" data-testid="dumper-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <PageHeader
          title="Dumper Intelligence"
          subtitle="Mining-grade analytics for BD-series heavy dumpers"
        />
        <div className="flex items-center gap-2">
          <select
            value={mine}
            onChange={(e) => setMine(e.target.value)}
            data-testid="dumper-mine-selector"
            className="px-3 py-2 rounded-lg text-[13px]"
            style={{
              background: "rgba(255,255,255,0.75)",
              border: "1px solid var(--glass-border-btm)",
              color: "var(--slate-900)",
            }}
          >
            <option value="all">All Mines</option>
            {mines.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
          <button
            onClick={downloadShiftReport}
            disabled={reportLoading}
            data-testid="dumper-shift-report-btn"
            className="px-3 py-2 rounded-lg flex items-center gap-2 text-white text-[13px] font-semibold"
            style={{
              background: "linear-gradient(135deg, #E8884A 0%, #C4622D 100%)",
              boxShadow: "0 6px 16px rgba(196,98,45,0.25)",
            }}
          >
            {reportLoading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Shift Report
          </button>
        </div>
      </div>

      {/* KPI strip — 6 clickable tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="dumper-kpi-strip">
        <ClickableKPITile testId="dumper-kpi-fleet"     label="Total Dumpers"  value={fmtNum(kpis?.total_dumpers)}        sub="BD-series fleet"                 color="slate"  onClick={() => setDrill("fleet")} />
        <ClickableKPITile testId="dumper-kpi-cycles"    label="Cycles Today"   value={fmtNum(kpis?.total_cycles_today)}   sub={`${kpis?.avg_cycles_per_shift || 0}/shift avg`} color="copper" onClick={() => setDrill("cycles")} />
        <ClickableKPITile testId="dumper-kpi-tonnes"    label="Tonnes Today"   value={fmtNum(kpis?.total_tonnes_today)}   sub="hauled"                          color="green"  onClick={() => setDrill("tonnes")} />
        <ClickableKPITile testId="dumper-kpi-fpt"      label="Fleet L/Tonne"  value={kpis?.fleet_fuel_per_tonne ?? "—"}  sub={`bench ${TKPH_BENCHMARK_LPT}`}    color={(kpis?.fleet_fuel_per_tonne || 0) > TKPH_BENCHMARK_LPT ? "amber" : "green"} onClick={() => setDrill("fpt")} />
        <ClickableKPITile
          testId="dumper-kpi-overloads"
          label="Overloads"
          value={fmtNum(kpis?.overload_incidents)}
          sub="today"
          color={kpis?.overload_incidents > 5 ? "red" : kpis?.overload_incidents > 0 ? "amber" : "green"}
          onClick={() => setDrill("overloads")}
        />
        <ClickableKPITile
          testId="dumper-kpi-tkph"
          label="TKPH Warnings"
          value={fmtNum(kpis?.tkph_warnings)}
          sub={`${fmtINR(kpis?.tyre_risk_inr)} tyre risk`}
          color={kpis?.tkph_warnings > 3 ? "red" : kpis?.tkph_warnings > 0 ? "amber" : "green"}
          onClick={() => setDrill("tkph")}
        />
      </div>

      {/* Row 1: Cycle breakdown + TKPH */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <GlassCard
          className="p-5 lg:col-span-3 cursor-pointer"
          data-testid="dumper-cycle-breakdown"
          onClick={() => setDrill("haul")}
        >
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-[14px] font-display font-semibold" style={{ color: "var(--slate-900)" }}>Haul Cycle Breakdown</div>
              <div className="text-[11.5px]" style={{ color: "var(--slate-500)" }}>Top 5 dumpers · where minutes are lost</div>
            </div>
            <ArrowUpRight size={14} style={{ color: "var(--slate-300)" }} />
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={cycleBreakdown} layout="vertical" margin={{ left: 30, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
              <XAxis type="number" tick={{ fontSize: 11, fill: COLORS.slate500 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: COLORS.slate500 }} width={110} />
              <Tooltip contentStyle={{ background: "rgba(255,255,255,0.95)", border: `1px solid ${COLORS.slate500}22`, borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Load" stackId="a" fill={STAGE_COLORS.Load} />
              <Bar dataKey="Haul" stackId="a" fill={STAGE_COLORS.Haul} />
              <Bar dataKey="Queue" stackId="a" fill={STAGE_COLORS.Queue} />
              <Bar dataKey="Return" stackId="a" fill={STAGE_COLORS.Return} />
            </BarChart>
          </ResponsiveContainer>
        </GlassCard>

        <GlassCard
          className="p-5 lg:col-span-2 cursor-pointer"
          data-testid="dumper-tkph-panel"
          onClick={() => setDrill("tyre")}
          style={{
            background:
              "linear-gradient(180deg, rgba(232,136,74,0.10) 0%, rgba(255,255,255,0.85) 60%)",
          }}
        >
          <div className="flex items-center justify-between mb-1">
            <div className="text-[14px] font-display font-semibold" style={{ color: "var(--slate-900)" }}>Tyre Health Index</div>
            <ArrowUpRight size={14} style={{ color: "var(--slate-300)" }} />
          </div>
          <div className="text-[11.5px] mb-3" style={{ color: "var(--slate-500)" }}>TKPH vs rated · {kpis?.tkph_warnings || 0} machines at risk · {fmtINR(kpis?.tyre_risk_inr || 0)}</div>
          <div className="space-y-1.5 max-h-[240px] overflow-y-auto pr-1">
            {tkphRows.slice(0, 10).map((m) => (
              <div key={m.serial_number} className="flex items-center justify-between text-[12px] py-1.5 px-2 rounded" style={{ background: "rgba(255,255,255,0.45)" }}>
                <span className="font-mono" style={{ color: "var(--slate-900)" }}>{m.serial_number.replace("BEML-", "")}</span>
                <div className="flex items-center gap-2 flex-1 justify-end">
                  <span className="text-[11px]" style={{ color: "var(--slate-500)" }}>{fmtNum(Math.round(m.tkph || 0))} / {fmtNum(m.rated_tkph)}</span>
                  <span className="text-[11px] font-semibold" style={{ color: m.status === "CRITICAL" ? COLORS.red : m.status === "WARNING" ? COLORS.amber : COLORS.green, minWidth: 36, textAlign: "right" }}>
                    {Math.round((m.ratio || 0) * 100)}%
                  </span>
                  <StatusBadge status={m.status === "OK" ? "active" : m.status === "WARNING" ? "warning" : "alert"} label={m.status} />
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>

      {/* Row 2: Fuel/tonne + Production vs Target */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <GlassCard
          className="p-5 cursor-pointer"
          data-testid="dumper-fuel-per-tonne"
          onClick={() => setDrill("fuel")}
        >
          <div className="flex items-center justify-between">
            <div className="text-[14px] font-display font-semibold" style={{ color: "var(--slate-900)" }}>Fuel per Tonne</div>
            <ArrowUpRight size={14} style={{ color: "var(--slate-300)" }} />
          </div>
          <div className="text-[11.5px] mb-3" style={{ color: "var(--slate-500)" }}>Industry benchmark · {TKPH_BENCHMARK_LPT} L/t</div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={fuelData} layout="vertical" margin={{ left: 30, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
              <XAxis type="number" tick={{ fontSize: 11, fill: COLORS.slate500 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: COLORS.slate500 }} width={110} />
              <Tooltip contentStyle={{ background: "rgba(255,255,255,0.95)", border: `1px solid ${COLORS.slate500}22`, borderRadius: 8, fontSize: 12 }} />
              <ReferenceLine x={TKPH_BENCHMARK_LPT} stroke={COLORS.red} strokeDasharray="4 4" label={{ value: `${TKPH_BENCHMARK_LPT}`, position: "top", fontSize: 11, fill: COLORS.red }} />
              <Bar dataKey="lpt" radius={[0, 4, 4, 0]}>
                {fuelData.map((d, i) => (
                  <Bar key={i} fill={d.lpt > TKPH_BENCHMARK_LPT ? COLORS.amber : COLORS.green} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </GlassCard>

        <GlassCard
          className="p-5 cursor-pointer"
          data-testid="dumper-production-trend"
          onClick={() => setDrill("production")}
        >
          <div className="flex items-center justify-between">
            <div className="text-[14px] font-display font-semibold" style={{ color: "var(--slate-900)" }}>Production vs Target</div>
            <ArrowUpRight size={14} style={{ color: "var(--slate-300)" }} />
          </div>
          <div className="text-[11.5px] mb-3" style={{ color: "var(--slate-500)" }}>7-day tonnage</div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={productionTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: COLORS.slate500 }} />
              <YAxis tick={{ fontSize: 11, fill: COLORS.slate500 }} />
              <Tooltip contentStyle={{ background: "rgba(255,255,255,0.95)", border: `1px solid ${COLORS.slate500}22`, borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="actual" stroke={COLORS.copper} strokeWidth={2.5} dot={{ r: 3, fill: COLORS.copper }} name="Actual (t)" />
              <Line type="monotone" dataKey="target" stroke={COLORS.slate500} strokeDasharray="5 5" strokeWidth={2} dot={false} name="Target (t)" />
            </LineChart>
          </ResponsiveContainer>
        </GlassCard>
      </div>

      {/* Overload incident log */}
      <GlassCard className="p-5" data-testid="dumper-overload-log">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[14px] font-display font-semibold" style={{ color: "var(--slate-900)" }}>Overload Incident Log</div>
            <div className="text-[11.5px]" style={{ color: "var(--slate-500)" }}>{overloads.length} incident{overloads.length !== 1 ? "s" : ""} · acknowledge to clear</div>
          </div>
          <AlertTriangle size={16} style={{ color: COLORS.amber }} />
        </div>
        {overloads.length === 0 ? (
          <div className="text-[12.5px] text-center py-6" style={{ color: "var(--slate-500)" }}>
            No overload incidents in the current window.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]" data-testid="dumper-overload-table">
              <thead>
                <tr style={{ color: "var(--slate-500)" }}>
                  <th className="text-left py-1.5 px-2">Machine</th>
                  <th className="text-left py-1.5 px-2">Time</th>
                  <th className="text-right py-1.5 px-2">Actual</th>
                  <th className="text-right py-1.5 px-2">Rated</th>
                  <th className="text-right py-1.5 px-2">Over by</th>
                  <th className="text-left py-1.5 px-2">Operator</th>
                  <th className="text-right py-1.5 px-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {overloads.slice(0, 12).map((o) => (
                  <tr
                    key={o.id}
                    className={o.id === stream.lastNewAlertId ? "alert-flash" : ""}
                    style={{ borderTop: "1px solid var(--glass-border-btm)" }}
                    data-testid={o._live ? "overload-row-live" : undefined}
                  >
                    <td className="py-1.5 px-2 font-mono" style={{ color: "var(--slate-900)" }}>{o.serial_number.replace("BEML-", "")}</td>
                    <td className="py-1.5 px-2" style={{ color: "var(--slate-700)" }}>{(o.ts || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="py-1.5 px-2 text-right font-mono" style={{ color: "var(--slate-900)" }}>{o.actual_payload_t}t</td>
                    <td className="py-1.5 px-2 text-right font-mono" style={{ color: "var(--slate-700)" }}>{o.rated_payload_t}t</td>
                    <td className="py-1.5 px-2 text-right font-mono" style={{ color: COLORS.red, fontWeight: 600 }}>+{o.over_by_pct}%</td>
                    <td className="py-1.5 px-2" style={{ color: "var(--slate-700)" }}>{o.operator}</td>
                    <td className="py-1.5 px-2 text-right">
                      {o.acknowledged ? (
                        <span style={{ color: COLORS.green, fontWeight: 600 }}>Acknowledged</span>
                      ) : (
                        <button
                          onClick={() => onAck(o.id)}
                          disabled={acking === o.id}
                          data-testid={`overload-ack-${o.id.slice(0, 8)}`}
                          className="px-2.5 py-1 rounded-md text-[11.5px] font-semibold"
                          style={{
                            background: "rgba(196,98,45,0.10)",
                            color: COLORS.copper,
                            border: `1px solid ${COLORS.copper}33`,
                          }}
                        >
                          {acking === o.id ? "…" : "Acknowledge"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      {/* Operator scorecard */}
      <GlassCard className="p-5" data-testid="dumper-operator-scorecard">
        <div className="text-[14px] font-display font-semibold mb-3" style={{ color: "var(--slate-900)" }}>Operator Performance Scorecard</div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr style={{ color: "var(--slate-500)" }}>
                <th className="text-left py-1.5 px-2">Operator</th>
                <th className="text-right py-1.5 px-2">Cycles</th>
                <th className="text-right py-1.5 px-2">Avg Payload</th>
                <th className="text-right py-1.5 px-2">Overloads</th>
                <th className="text-right py-1.5 px-2">Overspeeds</th>
                <th className="text-right py-1.5 px-2">Fuel/Tonne</th>
                <th className="text-right py-1.5 px-2">Score</th>
              </tr>
            </thead>
            <tbody>
              {operators.map((o, i) => {
                const total = operators.length;
                const tone = i < 3 ? COLORS.green : i >= total - 3 ? COLORS.red : COLORS.slate500;
                return (
                  <tr key={o.operator} style={{ borderTop: "1px solid var(--glass-border-btm)" }}>
                    <td className="py-1.5 px-2" style={{ color: "var(--slate-900)" }}>{o.operator}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{o.cycles}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{o.avg_payload_t}t</td>
                    <td className="py-1.5 px-2 text-right font-mono" style={{ color: o.overloads > 0 ? COLORS.red : "var(--slate-700)" }}>{o.overloads}</td>
                    <td className="py-1.5 px-2 text-right font-mono" style={{ color: o.overspeeds > 0 ? COLORS.amber : "var(--slate-700)" }}>{o.overspeeds}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{o.fuel_per_tonne}</td>
                    <td className="py-1.5 px-2 text-right font-mono" style={{ color: tone, fontWeight: 600 }}>{o.score}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </GlassCard>

      {/* ── Drill-down panel ─────────────────────────────────── */}
      <DrillDownPanel
        isOpen={!!drill}
        onClose={() => setDrill(null)}
        title={DRILL_META[drill]?.title || ""}
        subtitle={DRILL_META[drill]?.subtitle || ""}
        width={680}
        testId={`dumper-drill-${drill || "none"}`}
      >
        {drill === "fleet" && (
          <StatTableDrillDown
            rows={fleet}
            columns={[
              { key: "serial_number", label: "Machine", mono: true, format: (v) => v.replace("BEML-", "") },
              { key: "model", label: "Model" },
              { key: "mine_name", label: "Mine" },
              { key: "operator", label: "Operator" },
              { key: "engine_hours", label: "Engine Hrs", mono: true, format: (v) => fmtNum(v) },
            ]}
          />
        )}
        {drill === "cycles" && (
          <StatTableDrillDown
            rows={[...fleet].sort((a, b) => (b.cycles_today || 0) - (a.cycles_today || 0))}
            columns={[
              { key: "serial_number", label: "Machine", mono: true, format: (v) => v.replace("BEML-", "") },
              { key: "model", label: "Model" },
              { key: "cycles_today", label: "Cycles", mono: true, format: (v) => fmtNum(v) },
              { key: "avg_cycle_min", label: "Avg Cycle (min)", mono: true, format: (v) => v ?? "—" },
            ]}
          />
        )}
        {drill === "tonnes" && (
          <StatTableDrillDown
            rows={[...fleet].sort((a, b) => (b.tonnes_today || 0) - (a.tonnes_today || 0))}
            columns={[
              { key: "serial_number", label: "Machine", mono: true, format: (v) => v.replace("BEML-", "") },
              { key: "model", label: "Model" },
              { key: "tonnes_today", label: "Tonnes", mono: true, format: (v) => fmtNum(v) },
              { key: "cycles_today", label: "Cycles", mono: true },
              { key: "payload_rated_t", label: "Rated (t)", mono: true },
            ]}
          />
        )}
        {drill === "fpt" && (
          <StatTableDrillDown
            rows={[...fleet].sort((a, b) => (b.fuel_per_tonne || 0) - (a.fuel_per_tonne || 0))}
            columns={[
              { key: "serial_number", label: "Machine", mono: true, format: (v) => v.replace("BEML-", "") },
              { key: "model", label: "Model" },
              { key: "fuel_per_tonne", label: "L / Tonne", mono: true,
                format: (v) => (
                  <span style={{ color: v > TKPH_BENCHMARK_LPT ? COLORS.amber : COLORS.green, fontWeight: 600 }}>
                    {v ?? "—"}
                  </span>
                ),
              },
              { key: "tonnes_today", label: "Tonnes", mono: true, format: (v) => fmtNum(v) },
              { key: "fuel_benchmark_lpt", label: "Bench", mono: true, format: (v) => v ?? TKPH_BENCHMARK_LPT },
            ]}
          />
        )}
        {drill === "overloads" && (
          <StatTableDrillDown
            rows={overloads}
            emptyText="No overload incidents in the current window."
            columns={[
              { key: "serial_number", label: "Machine", mono: true, format: (v) => (v || "").replace("BEML-", "") },
              { key: "ts", label: "Time", format: (v) => (v || "").slice(0, 16).replace("T", " ") },
              { key: "actual_payload_t", label: "Actual", mono: true, format: (v) => `${v}t` },
              { key: "rated_payload_t", label: "Rated", mono: true, format: (v) => `${v}t` },
              { key: "over_by_pct", label: "Over by", mono: true,
                format: (v) => <span style={{ color: COLORS.red, fontWeight: 600 }}>+{v}%</span>,
              },
              { key: "operator", label: "Operator" },
              { key: "acknowledged", label: "Status",
                format: (v) => (v
                  ? <span style={{ color: COLORS.green, fontWeight: 600 }}>Acknowledged</span>
                  : <span style={{ color: COLORS.amber, fontWeight: 600 }}>Open</span>),
              },
            ]}
          />
        )}
        {drill === "tkph" && (
          <StatTableDrillDown
            rows={tkphRows.sort((a, b) => b.ratio - a.ratio)}
            columns={[
              { key: "serial_number", label: "Machine", mono: true, format: (v) => v.replace("BEML-", "") },
              { key: "model", label: "Model" },
              { key: "tkph", label: "TKPH", mono: true, format: (v) => fmtNum(Math.round(v || 0)) },
              { key: "rated_tkph", label: "Rated", mono: true, format: (v) => fmtNum(v) },
              { key: "ratio", label: "%", mono: true,
                format: (v, row) => (
                  <span style={{ color: row.status === "CRITICAL" ? COLORS.red : row.status === "WARNING" ? COLORS.amber : COLORS.green, fontWeight: 600 }}>
                    {Math.round((v || 0) * 100)}%
                  </span>
                ),
              },
              { key: "status", label: "Status",
                format: (v) => <StatusBadge status={v === "OK" ? "active" : v === "WARNING" ? "warning" : "alert"} label={v} />,
              },
            ]}
          />
        )}
        {drill === "haul" && (
          <StatTableDrillDown
            rows={[...fleet].sort((a, b) => (b.avg_cycle_min || 0) - (a.avg_cycle_min || 0))}
            columns={[
              { key: "serial_number", label: "Machine", mono: true, format: (v) => v.replace("BEML-", "") },
              { key: "model", label: "Model" },
              { key: "cycles_today", label: "Cycles", mono: true },
              { key: "avg_cycle_min", label: "Avg Cycle (min)", mono: true,
                format: (v) => <span style={{ color: v > 50 ? COLORS.amber : "var(--slate-700)", fontWeight: 600 }}>{v ?? "—"}</span>,
              },
              { key: "tonnes_today", label: "Tonnes", mono: true, format: (v) => fmtNum(v) },
            ]}
          />
        )}
        {drill === "tyre" && (
          <StatTableDrillDown
            rows={tkphRows.sort((a, b) => b.ratio - a.ratio)}
            columns={[
              { key: "serial_number", label: "Machine", mono: true, format: (v) => v.replace("BEML-", "") },
              { key: "model", label: "Model" },
              { key: "tkph", label: "TKPH", mono: true, format: (v) => fmtNum(Math.round(v || 0)) },
              { key: "rated_tkph", label: "Rated", mono: true, format: (v) => fmtNum(v) },
              { key: "tyre_count", label: "Tyres", mono: true },
              { key: "tyre_cost_inr", label: "Tyre cost", mono: true, format: (v) => fmtINR(v) },
              { key: "status", label: "Status",
                format: (v) => <StatusBadge status={v === "OK" ? "active" : v === "WARNING" ? "warning" : "alert"} label={v} />,
              },
            ]}
          />
        )}
        {drill === "fuel" && (
          <StatTableDrillDown
            rows={[...fleet].sort((a, b) => (b.fuel_per_tonne || 0) - (a.fuel_per_tonne || 0))}
            columns={[
              { key: "serial_number", label: "Machine", mono: true, format: (v) => v.replace("BEML-", "") },
              { key: "model", label: "Model" },
              { key: "fuel_per_tonne", label: "L / Tonne", mono: true,
                format: (v) => (
                  <span style={{ color: v > TKPH_BENCHMARK_LPT ? COLORS.amber : COLORS.green, fontWeight: 600 }}>
                    {v ?? "—"}
                  </span>
                ),
              },
              { key: "tonnes_today", label: "Tonnes", mono: true, format: (v) => fmtNum(v) },
              { key: "delta", label: "vs bench", mono: true,
                format: (_v, row) => {
                  const d = (row.fuel_per_tonne || 0) - TKPH_BENCHMARK_LPT;
                  const sign = d > 0 ? "+" : "";
                  return <span style={{ color: d > 0 ? COLORS.amber : COLORS.green }}>{sign}{d.toFixed(2)}</span>;
                },
              },
            ]}
          />
        )}
        {drill === "production" && (
          <StatTableDrillDown
            rows={productionTrend.map((d) => ({
              ...d,
              variance: d.actual - d.target,
              attainment: d.target ? Math.round((d.actual / d.target) * 100) : 0,
            }))}
            columns={[
              { key: "day", label: "Day" },
              { key: "actual", label: "Actual (t)", mono: true, format: (v) => fmtNum(v) },
              { key: "target", label: "Target (t)", mono: true, format: (v) => fmtNum(v) },
              { key: "variance", label: "Variance", mono: true,
                format: (v) => (
                  <span style={{ color: v >= 0 ? COLORS.green : COLORS.red, fontWeight: 600 }}>
                    {v >= 0 ? "+" : ""}{fmtNum(v)}
                  </span>
                ),
              },
              { key: "attainment", label: "Attainment", mono: true, format: (v) => `${v}%` },
            ]}
          />
        )}
      </DrillDownPanel>
    </div>
  );
}
